import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { triggerCreditRefresh } from '../components/CreditDisplay';
import { getUserLocations, addUserLocation, updateUserLocation, deleteUserLocation } from '../api';
import {
  trackLocationFilterToggled,
  trackLocationChipRemoved,
  trackZeroResultsShown,
  trackLocationAdded,
  trackLocationRemoved,
  trackLocationRadiusChanged
} from '../utils/analytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper to get match badge color
const getMatchBadgeColor = (score) => {
  if (score >= 80) return { bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.4)', text: 'var(--green-400)' };
  if (score >= 60) return { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.4)', text: 'var(--amber-400)' };
  return { bg: 'rgba(156, 163, 175, 0.15)', border: 'rgba(156, 163, 175, 0.4)', text: 'var(--text-tertiary)' };
};

// Build job description preview from structured data
const getJobDescriptionPreview = (job) => {
  const desc = job.job_description_structured;
  if (!desc) return '';

  // Return role summary if available, otherwise company overview, otherwise first responsibility
  if (desc.roleSummary) return desc.roleSummary;
  if (desc.companyOverview) return desc.companyOverview;
  if (desc.responsibilities?.length > 0) return desc.responsibilities[0];
  return '';
};

const DiscoverJobs = () => {
  const { user, session, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [generatingResume, setGeneratingResume] = useState(false);
  const [viewedJobs, setViewedJobs] = useState(() => {
    // Load viewed jobs from localStorage
    const stored = localStorage.getItem('viewedJobs');
    return stored ? JSON.parse(stored) : [];
  });

  // Recommended jobs state
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(true);

  // Filter states
  const [experienceLevel, setExperienceLevel] = useState('');
  const [jobType, setJobType] = useState('');
  const [daysAgo, setDaysAgo] = useState('');
  const [sponsorship, setSponsorship] = useState('');
  const [salaryMin, setSalaryMin] = useState('');

  // Location filter states
  const [savedLocations, setSavedLocations] = useState([]);
  const [excludedLocationIds, setExcludedLocationIds] = useState([]);
  const [locationFilterEnabled, setLocationFilterEnabled] = useState(false);

  // Location management states
  const [showLocationManager, setShowLocationManager] = useState(false);
  const [newLocationInput, setNewLocationInput] = useState('');
  const [newLocationRadius, setNewLocationRadius] = useState(25);
  const [addingLocation, setAddingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const radiusTimeoutRef = useRef({});

  useEffect(() => {
    if (!authLoading && session && user) {
      loadUserLocations();
      loadJobs();
      loadRecommendedJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, session, user, experienceLevel, jobType, daysAgo, sponsorship, locationFilterEnabled, excludedLocationIds]);

  const loadUserLocations = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const data = await getUserLocations(token);
      setSavedLocations(data.locations || []);
    } catch (err) {
      console.error('Failed to load user locations:', err);
      setSavedLocations([]);
    }
  };

  // Location management handlers
  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocationInput.trim()) return;

    const token = getToken();
    if (!token) return;

    try {
      setAddingLocation(true);
      setLocationError('');
      await addUserLocation(token, newLocationInput.trim(), newLocationRadius);
      trackLocationAdded(newLocationInput.trim(), newLocationRadius);
      setNewLocationInput('');
      setNewLocationRadius(25);
      await loadUserLocations();
    } catch (err) {
      console.error('Error adding location:', err);
      setLocationError(err.message || 'Failed to add location. Try a different city or zip code.');
    } finally {
      setAddingLocation(false);
    }
  };

  const handleUpdateRadius = async (locationId, radiusMiles) => {
    const token = getToken();
    if (!token) return;

    const location = savedLocations.find(loc => loc.id === locationId);
    const oldRadius = location?.radius_miles;

    // Clear pending timeout
    if (radiusTimeoutRef.current[locationId]) {
      clearTimeout(radiusTimeoutRef.current[locationId]);
    }

    // Optimistic update
    setSavedLocations(prev =>
      prev.map(loc =>
        loc.id === locationId ? { ...loc, radius_miles: radiusMiles } : loc
      )
    );

    // Debounced API call
    radiusTimeoutRef.current[locationId] = setTimeout(async () => {
      try {
        await updateUserLocation(token, locationId, radiusMiles);
        if (location && oldRadius !== radiusMiles) {
          trackLocationRadiusChanged(location.location_name, oldRadius, radiusMiles);
        }
      } catch (err) {
        console.error('Error updating radius:', err);
        // Revert on error
        setSavedLocations(prev =>
          prev.map(loc =>
            loc.id === locationId ? { ...loc, radius_miles: oldRadius } : loc
          )
        );
      }
    }, 500);
  };

  const handleDeleteLocation = async (locationId) => {
    const token = getToken();
    if (!token) return;

    const location = savedLocations.find(loc => loc.id === locationId);

    try {
      await deleteUserLocation(token, locationId);
      if (location) {
        trackLocationRemoved(location.location_name);
      }
      setSavedLocations(prev => prev.filter(loc => loc.id !== locationId));
      // Also remove from excluded if it was there
      setExcludedLocationIds(prev => prev.filter(id => id !== locationId));
    } catch (err) {
      console.error('Error deleting location:', err);
    }
  };

  // Track zero results when location filter is active
  useEffect(() => {
    if (!loading && locationFilterEnabled && jobs.length === 0 && savedLocations.length > 0) {
      const activeLocations = savedLocations
        .filter(loc => !excludedLocationIds.includes(loc.id))
        .map(loc => loc.location_name);
      const radiuses = savedLocations
        .filter(loc => !excludedLocationIds.includes(loc.id))
        .map(loc => loc.radius_miles);

      if (activeLocations.length > 0) {
        trackZeroResultsShown(activeLocations, radiuses, 'location');
      }
    }
  }, [loading, locationFilterEnabled, jobs.length, savedLocations, excludedLocationIds]);

  const loadRecommendedJobs = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoadingRecommended(true);
      const response = await fetch(`${API_URL}/api/jobs/recommended?limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recommended jobs');
      }

      const data = await response.json();
      setRecommendedJobs(data.jobs || []);
    } catch (err) {
      console.error('Failed to load recommended jobs:', err);
      setRecommendedJobs([]);
    } finally {
      setLoadingRecommended(false);
    }
  };

  const loadJobs = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (experienceLevel) params.append('experience_level', experienceLevel);
      if (jobType) params.append('job_type', jobType);
      if (daysAgo) params.append('days_ago', daysAgo);
      if (sponsorship) params.append('sponsorship', sponsorship);

      // Add location filter parameters
      if (locationFilterEnabled && savedLocations.length > 0) {
        // Only enable location mode if there are active (non-excluded) locations
        const activeLocationCount = savedLocations.length - excludedLocationIds.length;
        if (activeLocationCount > 0) {
          params.append('location_mode', 'saved');
          if (excludedLocationIds.length > 0) {
            params.append('exclude_locations', excludedLocationIds.join(','));
          }
        }
      }

      const url = `${API_URL}/api/jobs${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const data = await response.json();
      setJobs(data.jobs || []);
      setTotalJobs(data.total || 0);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setJobs([]);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateResume = async (jobId) => {
    const token = getToken();
    if (!token) return;

    try {
      setGeneratingResume(true);
      const response = await fetch(`${API_URL}/api/jobs/${jobId}/generate-resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate resume');
      }

      const data = await response.json();

      // Refresh credit display to show updated count
      triggerCreditRefresh();

      // Close the modal
      setSelectedJob(null);

      // Show success message and redirect to dashboard
      alert(`Resume generated successfully for ${data.company} - ${data.role}!\n\nCheck your applications in the Dashboard.`);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to generate resume:', err);
      alert(`Failed to generate resume: ${err.message}`);
    } finally {
      setGeneratingResume(false);
    }
  };

  const handleHideJob = async (jobId) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/jobs/${jobId}/hide`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to hide job');
      }

      // Remove job from list
      setJobs(jobs.filter(job => job.id !== jobId));
      setSelectedJob(null);
    } catch (err) {
      console.error('Failed to hide job:', err);
      alert(`Failed to hide job: ${err.message}`);
    }
  };

  const handleSearch = () => {
    loadJobs();
  };

  const markJobAsViewed = (jobId) => {
    if (!viewedJobs.includes(jobId)) {
      const updatedViewed = [...viewedJobs, jobId];
      setViewedJobs(updatedViewed);
      localStorage.setItem('viewedJobs', JSON.stringify(updatedViewed));
    }
  };

  const handleResetHiddenJobs = async () => {
    const token = getToken();
    if (!token) return;

    if (!window.confirm('Are you sure you want to reset all hidden jobs? This will show all jobs you previously hid.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/jobs/hidden/reset`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to reset hidden jobs');
      }

      const data = await response.json();
      alert(`Successfully reset ${data.count} hidden job(s)!`);

      // Reload jobs to show previously hidden jobs
      loadJobs();
    } catch (err) {
      console.error('Failed to reset hidden jobs:', err);
      alert(`Failed to reset hidden jobs: ${err.message}`);
    }
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <p style={{ color: 'var(--text-tertiary)' }}>Loading...</p>
      </div>
    );
  }

  if (!user || !session) {
    navigate('/login');
    return null;
  }

  // Helper to parse salary from compensation string (must be before filteredJobs)
  const parseSalary = (compensation) => {
    if (!compensation) return null;
    // Match patterns like "$51/hr", "$100,000", "$100k", etc.
    const hourlyMatch = compensation.match(/\$(\d+(?:,\d+)?)\s*(?:\/\s*(?:hr|hour|per hour))/i);
    if (hourlyMatch) {
      return parseInt(hourlyMatch[1].replace(',', ''), 10) * 2080; // Convert hourly to annual
    }
    const annualMatch = compensation.match(/\$(\d+(?:,\d+)?)\s*k?/i);
    if (annualMatch) {
      const value = parseInt(annualMatch[1].replace(',', ''), 10);
      return value < 1000 ? value * 1000 : value; // Handle both $100k and $100,000
    }
    return null;
  };

  // Filter jobs by salary (client-side) - must be before subtitle
  const filteredJobs = salaryMin
    ? jobs.filter(job => {
        const salary = parseSalary(job.compensation);
        return salary === null || salary >= parseInt(salaryMin, 10);
      })
    : jobs;

  const subtitle = !loading && totalJobs > 0
    ? `Find your next opportunity • ${filteredJobs.length} of ${totalJobs} jobs`
    : 'Find your next opportunity';

  // Skill tag styles
  const skillTagStyle = {
    padding: '3px 8px',
    background: 'rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.7rem',
    color: 'var(--indigo-400)',
    whiteSpace: 'nowrap'
  };

  const skillMoreStyle = {
    padding: '3px 8px',
    background: 'rgba(156, 163, 175, 0.1)',
    border: '1px solid rgba(156, 163, 175, 0.2)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.7rem',
    color: 'var(--text-tertiary)',
    whiteSpace: 'nowrap'
  };

  // Compensation badge style
  const compensationBadgeStyle = {
    padding: '4px 12px',
    background: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.75rem',
    color: 'var(--green-400)',
    fontWeight: '600'
  };

  // Sponsorship badge style
  const sponsorshipBadgeStyle = {
    padding: '4px 12px',
    background: 'rgba(139, 92, 246, 0.15)',
    border: '1px solid rgba(139, 92, 246, 0.3)',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.75rem',
    color: 'var(--purple-400)',
    fontWeight: '600'
  };

  // Helper to format sponsorship label
  const getSponsorshipLabel = (value) => {
    if (value === 'visa_sponsorship') return 'Visa Sponsored';
    if (value === 'security_clearance') return 'Security Clearance';
    return null;
  };

  return (
    <DashboardLayout
      title="Discover Jobs"
      subtitle={subtitle}
    >

        {/* Search Bar */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <input
            type="text"
            placeholder="Search by company, title, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={{
              width: '100%',
              padding: 'var(--space-md) var(--space-xl)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--text-primary)',
              fontSize: '1rem',
              outline: 'none',
              transition: 'all var(--duration-fast)',
              fontFamily: 'var(--font-body)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--indigo-500)';
              e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border-light)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-2xl)', flexWrap: 'wrap' }}>
          <select
            value={experienceLevel}
            onChange={(e) => setExperienceLevel(e.target.value)}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)'
            }}
          >
            <option value="">All Experience Levels</option>
            <option value="internship">Internship</option>
            <option value="entry">Entry Level</option>
            <option value="mid">Mid Level</option>
            <option value="senior">Senior Level</option>
            <option value="executive">Executive</option>
          </select>

          <select
            value={jobType}
            onChange={(e) => setJobType(e.target.value)}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)'
            }}
          >
            <option value="">All Job Types</option>
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
            <option value="contract">Contract</option>
            <option value="internship">Internship</option>
          </select>

          <select
            value={daysAgo}
            onChange={(e) => setDaysAgo(e.target.value)}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)'
            }}
          >
            <option value="">All Time</option>
            <option value="1">Past 24 hours</option>
            <option value="7">Past week</option>
            <option value="30">Past month</option>
          </select>

          <select
            value={salaryMin}
            onChange={(e) => setSalaryMin(e.target.value)}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)'
            }}
          >
            <option value="">Any Salary</option>
            <option value="40000">$40k+ / $19/hr+</option>
            <option value="50000">$50k+ / $24/hr+</option>
            <option value="60000">$60k+ / $29/hr+</option>
            <option value="70000">$70k+ / $34/hr+</option>
            <option value="80000">$80k+ / $38/hr+</option>
            <option value="90000">$90k+ / $43/hr+</option>
            <option value="100000">$100k+ / $48/hr+</option>
            <option value="110000">$110k+ / $53/hr+</option>
          </select>

          <select
            value={sponsorship}
            onChange={(e) => setSponsorship(e.target.value)}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              outline: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)'
            }}
          >
            <option value="">All Jobs</option>
            <option value="visa_sponsorship">Visa Sponsorship</option>
            <option value="security_clearance">Security Clearance</option>
          </select>

          {(experienceLevel || jobType || daysAgo || sponsorship || salaryMin || locationFilterEnabled) && (
            <button
              onClick={() => {
                setExperienceLevel('');
                setJobType('');
                setDaysAgo('');
                setSponsorship('');
                setSalaryMin('');
                setLocationFilterEnabled(false);
                setExcludedLocationIds([]);
              }}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-tertiary)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all var(--duration-fast)',
                fontFamily: 'var(--font-body)'
              }}
              onMouseOver={(e) => e.target.style.color = 'var(--text-primary)'}
              onMouseOut={(e) => e.target.style.color = 'var(--text-tertiary)'}
            >
              Clear Filters
            </button>
          )}

          <button
            onClick={handleResetHiddenJobs}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--red-500)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all var(--duration-fast)',
              marginLeft: 'auto',
              fontFamily: 'var(--font-body)'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.15)';
              e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.1)';
              e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
            }}
          >
            Reset Hidden Jobs
          </button>
        </div>

        {/* Location Filter Section */}
        <div style={{
          marginBottom: 'var(--space-xl)',
          padding: 'var(--space-md) var(--space-lg)',
          background: locationFilterEnabled ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-surface)',
          border: `1px solid ${locationFilterEnabled ? 'rgba(99, 102, 241, 0.3)' : 'var(--border-subtle)'}`,
          borderRadius: 'var(--radius-lg)',
          transition: 'all var(--duration-fast)'
        }}>
          {/* Header Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
            {/* Location Filter Toggle */}
            <button
              onClick={() => {
                if (savedLocations.length > 0) {
                  const newEnabled = !locationFilterEnabled;
                  setLocationFilterEnabled(newEnabled);
                  if (newEnabled) {
                    setExcludedLocationIds([]);
                  }
                  trackLocationFilterToggled(newEnabled, savedLocations.length);
                } else {
                  setShowLocationManager(true);
                }
              }}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                background: locationFilterEnabled ? 'var(--gradient-primary)' : 'var(--bg-elevated)',
                border: locationFilterEnabled ? 'none' : '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                color: locationFilterEnabled ? 'white' : 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all var(--duration-fast)',
                fontFamily: 'var(--font-body)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {savedLocations.length === 0 ? 'Add Target Locations' : locationFilterEnabled ? 'Location Filter On' : 'Filter by Location'}
            </button>

            {/* Location Chips (when filter is enabled) */}
            {locationFilterEnabled && savedLocations.length > 0 && (
              <>
                {savedLocations
                  .filter(loc => !excludedLocationIds.includes(loc.id))
                  .map(location => (
                    <div
                      key={location.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 8px 6px 12px',
                        background: 'rgba(99, 102, 241, 0.15)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.8rem',
                        color: 'var(--indigo-400)',
                        fontWeight: '500'
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      {location.location_name}
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '0.7rem' }}>
                        ({location.radius_miles}mi)
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newExcluded = [...excludedLocationIds, location.id];
                          setExcludedLocationIds(newExcluded);
                          trackLocationChipRemoved(location.location_name, savedLocations.length - newExcluded.length);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '2px',
                          cursor: 'pointer',
                          color: 'var(--indigo-400)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          transition: 'all var(--duration-fast)'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                        title={`Exclude ${location.location_name} from filter`}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}

                {excludedLocationIds.length > 0 && (
                  <button
                    onClick={() => setExcludedLocationIds([])}
                    style={{
                      padding: '6px 12px',
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-light)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '0.8rem',
                      color: 'var(--text-tertiary)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-body)'
                    }}
                  >
                    +{excludedLocationIds.length} hidden • Show all
                  </button>
                )}
              </>
            )}

            {/* Info text and manage button */}
            {savedLocations.length > 0 && !locationFilterEnabled && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
                {savedLocations.length} saved location{savedLocations.length !== 1 ? 's' : ''}
              </span>
            )}

            {/* Manage/Edit Locations Toggle */}
            <button
              onClick={() => setShowLocationManager(!showLocationManager)}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: 'none',
                fontSize: '0.8rem',
                color: 'var(--indigo-400)',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                marginLeft: savedLocations.length > 0 ? 'auto' : '0'
              }}
            >
              {showLocationManager ? 'Hide' : savedLocations.length > 0 ? 'Edit Locations' : 'Set Up'}
            </button>
          </div>

          {/* Expanded Location Manager */}
          {showLocationManager && (
            <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--border-subtle)' }}>
              {/* Error Message */}
              {locationError && (
                <div style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--red-500)',
                  fontSize: '0.8rem',
                  marginBottom: 'var(--space-md)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {locationError}
                  <button onClick={() => setLocationError('')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
                </div>
              )}

              {/* Saved Locations List */}
              {savedLocations.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                  {savedLocations.map(location => (
                    <div key={location.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-md)',
                      padding: 'var(--space-sm) var(--space-md)',
                      background: 'var(--bg-elevated)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--indigo-400)" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span style={{ fontWeight: '500', color: 'var(--text-primary)', minWidth: '120px' }}>
                        {location.location_name}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flex: 1 }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', width: '30px' }}>5mi</span>
                        <input
                          type="range"
                          min="5"
                          max="50"
                          value={location.radius_miles}
                          onChange={(e) => handleUpdateRadius(location.id, parseInt(e.target.value))}
                          style={{ flex: 1, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', width: '35px' }}>50mi</span>
                        <span style={{
                          padding: '2px 8px',
                          background: 'var(--indigo-500)',
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.7rem',
                          color: 'white',
                          fontWeight: '600',
                          minWidth: '40px',
                          textAlign: 'center'
                        }}>
                          {location.radius_miles}mi
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteLocation(location.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '4px',
                          cursor: 'pointer',
                          color: 'var(--text-tertiary)',
                          display: 'flex'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.color = 'var(--red-500)'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
                        title="Remove location"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"/>
                          <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Location Form */}
              {savedLocations.length < 5 && (
                <form onSubmit={handleAddLocation} style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Add city, state or zip code..."
                    value={newLocationInput}
                    onChange={(e) => setNewLocationInput(e.target.value)}
                    disabled={addingLocation}
                    style={{
                      flex: '1',
                      minWidth: '180px',
                      padding: 'var(--space-sm) var(--space-md)',
                      background: 'var(--bg-base)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem'
                    }}
                  />
                  <select
                    value={newLocationRadius}
                    onChange={(e) => setNewLocationRadius(parseInt(e.target.value))}
                    disabled={addingLocation}
                    style={{
                      padding: 'var(--space-sm) var(--space-md)',
                      background: 'var(--bg-base)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value={5}>5 mi</option>
                    <option value={10}>10 mi</option>
                    <option value={15}>15 mi</option>
                    <option value={25}>25 mi</option>
                    <option value={35}>35 mi</option>
                    <option value={50}>50 mi</option>
                  </select>
                  <button
                    type="submit"
                    disabled={addingLocation || !newLocationInput.trim()}
                    style={{
                      padding: 'var(--space-sm) var(--space-lg)',
                      background: addingLocation || !newLocationInput.trim() ? 'var(--bg-surface)' : 'var(--gradient-primary)',
                      color: addingLocation || !newLocationInput.trim() ? 'var(--text-tertiary)' : 'white',
                      border: addingLocation || !newLocationInput.trim() ? '1px solid var(--border-subtle)' : 'none',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: addingLocation || !newLocationInput.trim() ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-body)'
                    }}
                  >
                    {addingLocation ? 'Adding...' : 'Add'}
                  </button>
                </form>
              )}

              {savedLocations.length >= 5 && (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', margin: 0 }}>
                  Maximum 5 locations. Remove one to add another.
                </p>
              )}

              {/* Help text */}
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-md)', marginBottom: 0 }}>
                Save up to 5 locations. Jobs from all your locations appear together. Remote jobs are always included.
              </p>
            </div>
          )}
        </div>

        {/* For You Section - Recommended Jobs (hidden when filters are active) */}
        {!loadingRecommended && recommendedJobs.length > 0 && !experienceLevel && !jobType && !daysAgo && !sponsorship && !salaryMin && !searchQuery && !locationFilterEnabled && (
          <div style={{ marginBottom: 'var(--space-2xl)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.25rem',
                fontWeight: '700',
                color: 'var(--text-primary)',
                margin: 0
              }}>
                Recommended for You
              </h2>
              <span style={{
                padding: '4px 10px',
                background: 'linear-gradient(135deg, var(--indigo-500) 0%, var(--purple-500) 100%)',
                borderRadius: 'var(--radius-full)',
                fontSize: '0.75rem',
                color: 'white',
                fontWeight: '600'
              }}>
                Based on your profile
              </span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
              gap: 'var(--space-md)'
            }}>
              {recommendedJobs.map(job => {
                const matchColors = job.match_score ? getMatchBadgeColor(job.match_score) : null;
                return (
                  <div
                    key={`rec-${job.id}`}
                    style={{
                      padding: 'var(--space-lg)',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--indigo-500)',
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'all var(--duration-fast)',
                      position: 'relative'
                    }}
                    onClick={() => {
                      markJobAsViewed(job.id);
                      setSelectedJob(job);
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'var(--bg-elevated)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'var(--bg-surface)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Match Score Badge */}
                    {job.match_score && matchColors && (
                      <div style={{
                        position: 'absolute',
                        top: 'var(--space-sm)',
                        right: 'var(--space-sm)',
                        padding: '4px 10px',
                        background: matchColors.bg,
                        border: `1px solid ${matchColors.border}`,
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        color: matchColors.text
                      }}>
                        {job.match_score}% Match
                      </div>
                    )}
                    <h4 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1rem',
                      fontWeight: '700',
                      marginBottom: '4px',
                      color: 'var(--text-primary)',
                      paddingRight: '80px'
                    }}>
                      {job.job_title}
                    </h4>
                    <p style={{ fontSize: '0.875rem', color: 'var(--indigo-400)', fontWeight: '600', marginBottom: '4px' }}>
                      {job.company_name}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      {job.location}
                      {job.distance_miles !== null && job.distance_miles !== undefined && job.matched_location && (
                        <span style={{
                          padding: '2px 6px',
                          background: 'rgba(34, 211, 238, 0.12)',
                          border: '1px solid rgba(34, 211, 238, 0.25)',
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.65rem',
                          color: 'var(--cyan-400)',
                          fontWeight: '500'
                        }}>
                          {Math.round(job.distance_miles)} mi
                        </span>
                      )}
                    </p>
                    {/* Skills tags for recommended jobs */}
                    {job.skills?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {job.skills.slice(0, 4).map((skill, idx) => (
                          <span key={idx} style={skillTagStyle}>{skill}</span>
                        ))}
                        {job.skills.length > 4 && (
                          <span style={skillMoreStyle}>+{job.skills.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Jobs Grid */}
        {loading ? (
          <p style={{ color: 'var(--text-tertiary)' }}>Loading jobs...</p>
        ) : jobs.length === 0 ? (
          <div style={{
            padding: 'var(--space-4xl)',
            textAlign: 'center',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: locationFilterEnabled ? 'linear-gradient(135deg, var(--cyan-500) 0%, var(--indigo-500) 100%)' : 'var(--gradient-primary)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)'
            }}>
              {locationFilterEnabled ? (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              ) : (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              )}
            </div>
            {locationFilterEnabled ? (
              <>
                <p style={{ fontSize: '1.125rem', color: 'var(--text-primary)', marginBottom: 'var(--space-sm)', fontWeight: '600' }}>
                  No jobs found in your selected locations
                </p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-lg)' }}>
                  Try expanding your search:
                </p>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 auto',
                  maxWidth: '320px',
                  textAlign: 'left'
                }}>
                  <li style={{
                    padding: 'var(--space-sm) 0',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    <span style={{ color: 'var(--indigo-400)' }}>•</span>
                    <button
                      onClick={() => setShowLocationManager(true)}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--indigo-400)', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'var(--font-body)' }}
                    >
                      Increase your search radius
                    </button>
                  </li>
                  <li style={{
                    padding: 'var(--space-sm) 0',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    <span style={{ color: 'var(--indigo-400)' }}>•</span>
                    <button
                      onClick={() => setShowLocationManager(true)}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--indigo-400)', cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'var(--font-body)' }}
                    >
                      Add more target cities
                    </button>
                  </li>
                  <li style={{
                    padding: 'var(--space-sm) 0',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    <span style={{ color: 'var(--indigo-400)' }}>•</span>
                    <button
                      onClick={() => setLocationFilterEnabled(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--indigo-400)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontFamily: 'var(--font-body)'
                      }}
                    >
                      Turn off location filter to see all jobs
                    </button>
                  </li>
                </ul>
              </>
            ) : (
              <p style={{ fontSize: '1.125rem', color: 'var(--text-tertiary)' }}>
                {searchQuery ? `No jobs found matching "${searchQuery}"` : 'No jobs available at the moment'}
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 'var(--space-xl)' }}>
            {filteredJobs.map(job => {
              const matchColors = job.match_score ? getMatchBadgeColor(job.match_score) : null;
              const descriptionPreview = getJobDescriptionPreview(job);
              return (
              <div
                key={job.id}
                style={{
                  padding: 'var(--space-xl)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-xl)',
                  cursor: 'pointer',
                  transition: 'all var(--duration-base) var(--ease-out)',
                  position: 'relative'
                }}
                onClick={() => {
                  markJobAsViewed(job.id);
                  setSelectedJob(job);
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--bg-elevated)';
                  e.currentTarget.style.borderColor = 'var(--indigo-500)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--bg-surface)';
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Match Score Badge */}
                {job.match_score && matchColors && (
                  <div style={{
                    position: 'absolute',
                    top: 'var(--space-md)',
                    right: 'var(--space-md)',
                    padding: '4px 10px',
                    background: matchColors.bg,
                    border: `1px solid ${matchColors.border}`,
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    color: matchColors.text
                  }}>
                    {job.match_score}% Match
                  </div>
                )}
                <div style={{ marginBottom: 'var(--space-md)', paddingRight: job.match_score ? '90px' : '0' }}>
                  <h3 style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    marginBottom: 'var(--space-xs)',
                    color: 'var(--text-primary)'
                  }}>
                    {job.job_title}
                  </h3>
                  <p style={{ fontSize: '1rem', color: 'var(--indigo-400)', fontWeight: '600' }}>
                    {job.company_name}
                  </p>
                </div>
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {job.location}
                    {/* Distance badge when location filter is active */}
                    {job.distance_miles !== null && job.distance_miles !== undefined && job.matched_location && (
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(34, 211, 238, 0.12)',
                        border: '1px solid rgba(34, 211, 238, 0.25)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.7rem',
                        color: 'var(--cyan-400)',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}>
                        {Math.round(job.distance_miles)} mi from {job.matched_location}
                      </span>
                    )}
                  </p>
                  <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)', flexWrap: 'wrap' }}>
                    {job.experience_level && (
                      <span style={{
                        padding: '4px 12px',
                        background: 'rgba(99, 102, 241, 0.15)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.75rem',
                        color: 'var(--indigo-400)',
                        textTransform: 'capitalize'
                      }}>
                        {job.experience_level}
                      </span>
                    )}
                    {job.job_type && (
                      <span style={{
                        padding: '4px 12px',
                        background: 'rgba(245, 158, 11, 0.15)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.75rem',
                        color: 'var(--amber-400)',
                        textTransform: 'capitalize'
                      }}>
                        {job.job_type}
                      </span>
                    )}
                    {/* Compensation Badge */}
                    {job.compensation && (
                      <span style={compensationBadgeStyle}>
                        {job.compensation}
                      </span>
                    )}
                    {/* Sponsorship Badge */}
                    {job.sponsorship_and_legal && getSponsorshipLabel(job.sponsorship_and_legal) && (
                      <span style={sponsorshipBadgeStyle}>
                        {getSponsorshipLabel(job.sponsorship_and_legal)}
                      </span>
                    )}
                    {job.has_applied && (
                      <span style={{
                        padding: '4px 12px',
                        background: 'rgba(34, 197, 94, 0.15)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.75rem',
                        color: 'var(--green-400)',
                        fontWeight: '600'
                      }}>
                        Applied
                      </span>
                    )}
                    {!viewedJobs.includes(job.id) && (
                      <span style={{
                        padding: '4px 12px',
                        background: 'rgba(34, 211, 238, 0.15)',
                        border: '1px solid rgba(34, 211, 238, 0.3)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.75rem',
                        color: 'var(--cyan-400)',
                        fontWeight: '600'
                      }}>
                        New
                      </span>
                    )}
                  </div>
                </div>

                {/* Skills Tags */}
                {job.skills?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: 'var(--space-md)' }}>
                    {job.skills.slice(0, 5).map((skill, idx) => (
                      <span key={idx} style={skillTagStyle}>{skill}</span>
                    ))}
                    {job.skills.length > 5 && (
                      <span style={skillMoreStyle}>+{job.skills.length - 5} more</span>
                    )}
                  </div>
                )}

                {/* Description Preview */}
                {descriptionPreview && (
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--text-tertiary)',
                    lineHeight: '1.6',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {descriptionPreview}
                  </p>
                )}

                <div style={{ marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--border-subtle)' }}>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Posted {new Date(job.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              );
            })}
          </div>
        )}

      {/* Job Details Modal */}
      {selectedJob && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-md)'
          }}
          onClick={() => setSelectedJob(null)}
        >
          <div
            style={{
              maxWidth: 'min(800px, calc(100vw - 2rem))',
              maxHeight: '90vh',
              width: '100%',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-2xl)',
              padding: 'var(--space-lg)',
              overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.75rem',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'var(--text-primary)'
              }}>
                {selectedJob.job_title}
              </h2>
              <p style={{
                fontSize: '1.25rem',
                color: 'var(--indigo-400)',
                fontWeight: '600',
                marginBottom: 'var(--space-sm)'
              }}>
                {selectedJob.company_name}
              </p>
              <p style={{
                fontSize: '1rem',
                color: 'var(--text-tertiary)',
                marginBottom: 'var(--space-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                {selectedJob.location}
                {/* Distance badge in modal */}
                {selectedJob.distance_miles !== null && selectedJob.distance_miles !== undefined && selectedJob.matched_location && (
                  <span style={{
                    padding: '4px 10px',
                    background: 'rgba(34, 211, 238, 0.12)',
                    border: '1px solid rgba(34, 211, 238, 0.25)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.8rem',
                    color: 'var(--cyan-400)',
                    fontWeight: '500'
                  }}>
                    {Math.round(selectedJob.distance_miles)} mi from {selectedJob.matched_location}
                  </span>
                )}
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
                {selectedJob.experience_level && (
                  <span style={{
                    padding: '6px 16px',
                    background: 'rgba(99, 102, 241, 0.15)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.875rem',
                    color: 'var(--indigo-400)',
                    textTransform: 'capitalize'
                  }}>
                    {selectedJob.experience_level}
                  </span>
                )}
                {selectedJob.job_type && (
                  <span style={{
                    padding: '6px 16px',
                    background: 'rgba(245, 158, 11, 0.15)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.875rem',
                    color: 'var(--amber-400)',
                    textTransform: 'capitalize'
                  }}>
                    {selectedJob.job_type}
                  </span>
                )}
                {/* Compensation Badge in Modal */}
                {selectedJob.compensation && (
                  <span style={{
                    padding: '6px 16px',
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.875rem',
                    color: 'var(--green-400)',
                    fontWeight: '600'
                  }}>
                    {selectedJob.compensation}
                  </span>
                )}
                {/* Sponsorship Badge in Modal */}
                {selectedJob.sponsorship_and_legal && getSponsorshipLabel(selectedJob.sponsorship_and_legal) && (
                  <span style={{
                    padding: '6px 16px',
                    background: 'rgba(139, 92, 246, 0.15)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.875rem',
                    color: 'var(--purple-400)',
                    fontWeight: '600'
                  }}>
                    {getSponsorshipLabel(selectedJob.sponsorship_and_legal)}
                  </span>
                )}
                {selectedJob.has_applied && (
                  <span style={{
                    padding: '6px 16px',
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.875rem',
                    color: 'var(--green-400)',
                    fontWeight: '600'
                  }}>
                    Applied
                  </span>
                )}
                {!viewedJobs.includes(selectedJob.id) && (
                  <span style={{
                    padding: '6px 16px',
                    background: 'rgba(34, 211, 238, 0.15)',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.875rem',
                    color: 'var(--cyan-400)',
                    fontWeight: '600'
                  }}>
                    New
                  </span>
                )}
              </div>
              {/* Skills in modal header */}
              {selectedJob.skills?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedJob.skills.map((skill, idx) => (
                    <span key={idx} style={{
                      padding: '4px 12px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '0.8rem',
                      color: 'var(--indigo-400)'
                    }}>
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Structured Job Description */}
            {selectedJob.job_description_structured && (
              <div style={{
                padding: 'var(--space-lg)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 'var(--space-xl)'
              }}>
                {/* Company Overview */}
                {selectedJob.job_description_structured.companyOverview && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      About the Company
                    </h3>
                    <p style={{
                      fontSize: '0.95rem',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.companyOverview}
                    </p>
                  </div>
                )}

                {/* Role Summary */}
                {selectedJob.job_description_structured.roleSummary && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      Role Summary
                    </h3>
                    <p style={{
                      fontSize: '0.95rem',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.roleSummary}
                    </p>
                  </div>
                )}

                {/* Responsibilities */}
                {selectedJob.job_description_structured.responsibilities?.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      Responsibilities
                    </h3>
                    <ul style={{
                      margin: 0,
                      paddingLeft: 'var(--space-lg)',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.responsibilities.map((item, i) => (
                        <li key={i} style={{ marginBottom: '6px', fontSize: '0.95rem' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Required Qualifications */}
                {selectedJob.job_description_structured.requiredQualifications?.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      Required Qualifications
                    </h3>
                    <ul style={{
                      margin: 0,
                      paddingLeft: 'var(--space-lg)',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.requiredQualifications.map((item, i) => (
                        <li key={i} style={{ marginBottom: '6px', fontSize: '0.95rem' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preferred Qualifications */}
                {selectedJob.job_description_structured.preferredQualifications?.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      Preferred Qualifications
                    </h3>
                    <ul style={{
                      margin: 0,
                      paddingLeft: 'var(--space-lg)',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.preferredQualifications.map((item, i) => (
                        <li key={i} style={{ marginBottom: '6px', fontSize: '0.95rem' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedJob.job_description_structured.benefits && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      Benefits & Compensation
                    </h3>
                    <p style={{
                      fontSize: '0.95rem',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.benefits}
                    </p>
                  </div>
                )}

                {/* Additional Requirements */}
                {selectedJob.job_description_structured.additionalRequirements && (
                  <div>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      marginBottom: 'var(--space-sm)',
                      color: 'var(--text-primary)'
                    }}>
                      Additional Requirements
                    </h3>
                    <p style={{
                      fontSize: '0.95rem',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.8'
                    }}>
                      {selectedJob.job_description_structured.additionalRequirements}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
              <button
                onClick={() => handleGenerateResume(selectedJob.id)}
                disabled={generatingResume}
                style={{
                  padding: 'var(--space-md) var(--space-xl)',
                  background: generatingResume
                    ? 'var(--bg-surface)'
                    : 'linear-gradient(135deg, var(--amber-500) 0%, var(--amber-400) 100%)',
                  color: generatingResume ? 'var(--text-tertiary)' : '#000',
                  border: generatingResume ? '1px solid var(--border-light)' : 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: '1rem',
                  fontWeight: '700',
                  fontFamily: 'var(--font-body)',
                  textAlign: 'center',
                  cursor: generatingResume ? 'not-allowed' : 'pointer',
                  transition: 'all var(--duration-fast)',
                  boxShadow: generatingResume ? 'none' : '0 4px 12px rgba(245, 158, 11, 0.3)',
                  opacity: generatingResume ? 0.7 : 1
                }}
              >
                {generatingResume ? 'Generating Resume...' : 'Auto-Generate Resume'}
              </button>
              <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
                <a
                  href={selectedJob.job_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1,
                    padding: 'var(--space-md) var(--space-xl)',
                    background: 'var(--gradient-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1rem',
                    fontWeight: '700',
                    fontFamily: 'var(--font-body)',
                    textAlign: 'center',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all var(--duration-fast)',
                    boxShadow: 'var(--glow-indigo)'
                  }}
                >
                  Apply Now
                </a>
                <button
                  onClick={() => setSelectedJob(null)}
                  disabled={generatingResume}
                  style={{
                    padding: 'var(--space-md) var(--space-xl)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    fontFamily: 'var(--font-body)',
                    cursor: generatingResume ? 'not-allowed' : 'pointer',
                    transition: 'all var(--duration-fast)',
                    opacity: generatingResume ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!generatingResume) {
                      e.currentTarget.style.background = 'var(--bg-elevated)';
                      e.currentTarget.style.borderColor = 'var(--border-medium)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--bg-surface)';
                    e.currentTarget.style.borderColor = 'var(--border-light)';
                  }}
                >
                  Close
                </button>
              </div>
              <button
                onClick={() => handleHideJob(selectedJob.id)}
                disabled={generatingResume}
                style={{
                  padding: 'var(--space-sm) var(--space-xl)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: 'var(--red-500)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  fontFamily: 'var(--font-body)',
                  cursor: generatingResume ? 'not-allowed' : 'pointer',
                  transition: 'all var(--duration-fast)',
                  opacity: generatingResume ? 0.5 : 1
                }}
                onMouseOver={(e) => {
                  if (!generatingResume) {
                    e.target.style.background = 'rgba(239, 68, 68, 0.15)';
                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                }}
              >
                Not Interested
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default DiscoverJobs;
